/**
 * PayPal / Wam error classification for checkout copy.
 * Returns null when the error is a buyer cancel (closed popup) or carries nothing to say.
 */
const CANCEL_PATTERNS = [
  /window closed/i,
  /closed before response/i,
  /popup.*clos/i,
  /clos.*popup/i,
  /window.*clos/i,
  /detected popup close/i,
  /user cancel/i,
  /cancel(l)?ed by (the )?user/i,
  /component (was )?(closed|destroyed)/i,
  /zoid destroyed/i,
];

const FRIENDLY: Array<{ match: RegExp; message: string }> = [
  { match: /popup.*block|blocked.*popup|window\.open/i, message: "Your browser blocked the PayPal window. Allow popups and try again." },
  { match: /instrument_declined|card.*declin|declin/i, message: "Your card was declined. Try another card or PayPal." },
  { match: /insufficient/i, message: "The payment could not go through. Try another card or PayPal." },
  { match: /expired/i, message: "This checkout expired. Try again." },
  { match: /card details/i, message: "Check your card details and try again." },
  { match: /invalid.*(card|number|cvv|expir)|validation|unprocessable/i, message: "Check your card details and try again." },
  { match: /network|failed to fetch|timeout|timed out|offline/i, message: "Connection problem. Check your internet and try again." },
  { match: /could not load|script/i, message: "PayPal could not load. Reload to try again." },
];

function rawMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message ?? "";
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message?: unknown }).message;
    return typeof m === "string" ? m : "";
  }
  return "";
}

export function isPaymentCancel(error: unknown): boolean {
  const raw = rawMessage(error);
  return CANCEL_PATTERNS.some((p) => p.test(raw));
}

export function friendlyPaymentError(error: unknown, fallback = "Payment could not be completed. Try again."): string | null {
  if (isPaymentCancel(error)) return null;
  const raw = rawMessage(error).trim();
  for (const f of FRIENDLY) if (f.match.test(raw)) return f.message;
  return fallback;
}
